import { Badge } from "@/components/ui/Badge";
import * as shared from "@/styles/shared";
import api from "@shared/api/client";
import type { AdminScrapingSchedule } from "@shared/types/scraping";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as s from "./AdminScrapingSchedulesTable.styles";

const AdminScrapingSchedulesTable = () => {
  const { t } = useTranslation("adminSettings");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminScrapingSchedule[]>([]);

  useEffect(() => {
    api.scrapingSchedule
      .getAll()
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(t("errorLoading"));
        setLoading(false);
      });
  }, []);

  return (
    <div className={shared.sectionCard}>
      <h2 className={shared.sectionHeading}>
        <Clock className={`${s.icon} ${shared.iconPrimary}`} /> {t("scrapingSchedules")}
      </h2>

      {loading && <p className={s.loading}>{t("loading")}</p>}

      {error && (
        <div className={s.errorCard}>
          <XCircle className={`${s.icon} ${s.errorIcon}`} />
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && !data?.length && <p className={s.empty}>{t("noUsers")}</p>}

      {!loading && !error && !!data?.length && (
        <>
          <div className={s.mobileGrid}>
            {data.map((u) => (
              <div key={u.userId} className={s.mobileCard}>
                <div className={s.mobileCardRow}>
                  <span className={s.mobileCardLabel}>{t("user")}</span>
                  <span className={shared.titleSmSemibold}>{u.name || t("dash")}</span>
                </div>
                <div className={s.mobileCardRow}>
                  <span className={s.mobileCardLabel}>{t("email")}</span>
                  <span className={s.mobileCardValue}>{u.email}</span>
                </div>
                <div className={s.mobileCardRow}>
                  <span className={s.mobileCardLabel}>{t("scrapingTime")}</span>
                  <span>
                    {u.scrapingTime ? (
                      <span className={s.timeBadge}>
                        <Clock className={`${s.badgeIcon}`} /> {u.scrapingTime}
                      </span>
                    ) : (
                      t("dash")
                    )}
                  </span>
                </div>
                <div className={s.mobileCardRow}>
                  <span className={s.mobileCardLabel}>{t("status")}</span>
                  {u.isScheduled ? (
                    <Badge variant="success" size="sm">
                      <CheckCircle2 className={s.badgeIconSmall} />
                      {t("scheduled")}
                    </Badge>
                  ) : (
                    <Badge variant="default" size="sm">
                      {t("notConfigured")}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>{t("user")}</th>
                  <th className={s.th}>{t("email")}</th>
                  <th className={s.th}>{t("scrapingTime")}</th>
                  <th className={s.th}>{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.userId} className={s.tr}>
                    <td className={s.td}>{u.name || t("dash")}</td>
                    <td className={s.td}>{u.email}</td>
                    <td className={s.td}>
                      {u.scrapingTime ? (
                        <span className={s.timeBadge}>
                          <Clock className={`${s.badgeIcon}`} /> {u.scrapingTime}
                        </span>
                      ) : (
                        t("dash")
                      )}
                    </td>
                    <td className={s.td}>
                      {u.isScheduled ? (
                        <Badge variant="success" size="sm">
                          <CheckCircle2 className={s.badgeIconSmall} />
                          {t("scheduled")}
                        </Badge>
                      ) : (
                        <Badge variant="default" size="sm">
                          {t("notConfigured")}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export { AdminScrapingSchedulesTable };
